import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-K: route the maintenance-plan `next_due_at` fallback through
// the canonical clock so the persisted lifecycle value is
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { useAuth } from '@/contexts/AuthContext';
import { useOptiAssets } from '@/hooks/useOptiAssets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CalendarPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface MaintenancePlanFormProps {
  assetId: string;
  trigger?: React.ReactNode;
}

export function MaintenancePlanForm({ assetId, trigger }: MaintenancePlanFormProps) {
  const { activeShop } = useAuth();
  const { canEdit } = useOptiAssets();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [intervalDays, setIntervalDays] = useState('90');
  const [nextDueAt, setNextDueAt] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!activeShop || !title || !intervalDays) throw new Error('Missing required fields');

      const { error } = await supabase
        .from('shop_asset_maintenance_plans')
        .insert({
          asset_id: assetId,
          shop_id: activeShop.id,
          name: title,
          description: description || null,
          interval_days: parseInt(intervalDays),
          next_due_at: nextDueAt || clock.now().toISOString(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-maintenance', assetId] });
      setOpen(false);
      resetForm();
      toast.success('Maintenance plan created');
    },
    onError: (error) => {
      toast.error('Failed to create plan: ' + error.message);
    },
  });

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setIntervalDays('90');
    setNextDueAt('');
  };

  if (!canEdit) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="gap-2">
            <CalendarPlus className="h-4 w-4" />
            Add Plan
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Maintenance Plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Annual Calibration"
            />
          </div>

          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Details about this maintenance..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Interval (days) *</Label>
              <Input
                type="number"
                min="1"
                value={intervalDays}
                onChange={(e) => setIntervalDays(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {parseInt(intervalDays) >= 365
                  ? `≈ ${(parseInt(intervalDays) / 365).toFixed(1)} years`
                  : parseInt(intervalDays) >= 30
                  ? `≈ ${Math.round(parseInt(intervalDays) / 30)} months`
                  : `${intervalDays} days`}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Next Due Date</Label>
              <Input
                type="date"
                value={nextDueAt}
                onChange={(e) => setNextDueAt(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={() => createMutation.mutate()}
            disabled={!title || !intervalDays || createMutation.isPending}
            className="w-full"
          >
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
